import { DecisionActionRequest, DecisionActionResult } from "../../models/home";
import { HomeStreamEvent, HomeStreamEventType } from "../../services/home-ia/home-stream.service";
import { HomeDecisionRefreshPlanner } from "./home-decision-refresh.planner";
import { HomeAutoRefreshTarget, PendingActionEchoContext } from "./home.component.state";

export class HomeDecisionActionEchoTracker {
  private readonly ttlMs = 5000;
  context: PendingActionEchoContext | null = null;

  constructor(private readonly planner: HomeDecisionRefreshPlanner) {}

  remember(
    decisionId: string,
    request: DecisionActionRequest,
    result: DecisionActionResult,
    refreshTargets: readonly HomeAutoRefreshTarget[]
  ): void {
    const targets = new Set(refreshTargets);
    const suppressed = new Set<HomeStreamEventType>();
    const decisionPatched = this.planner.canPatchDecisionSlicesLocally(result);
    const detailCovered = decisionPatched || targets.has("detail") || !this.planner.shouldRefreshDetail(decisionId);
    const listCovered = decisionPatched || (targets.has("list") && targets.has("calendar"));
    const overviewCovered = !!result.updatedSummary || targets.has("overview");
    const nextCovered = decisionPatched || targets.has("next");
    const datesCovered = !this.planner.needsDatesRefresh(result) || targets.has("dates");
    if (overviewCovered && listCovered) suppressed.add("decision.listChanged");
    if (overviewCovered && listCovered && detailCovered && datesCovered) suppressed.add("decision.changed");
    if (overviewCovered && nextCovered) suppressed.add("decision.nextChanged");
    if (targets.has("activity") && detailCovered && (result.activityId || request.clientTraceId)) suppressed.add("decision.activityAdded");
    if (targets.has("pulse") && targets.has("metrics")) {
      suppressed.add("pulse.stateChanged");
      suppressed.add("decision.metricsChanged");
    }
    this.context = suppressed.size ? {
      decisionId, clientTraceId: request.clientTraceId ?? undefined, activityId: result.activityId ?? undefined,
      expiresAt: Date.now() + this.ttlMs, suppressedEvents: suppressed
    } : null;
  }

  shouldSuppress(event: HomeStreamEvent): boolean {
    const context = this.context;
    if (!context) return false;
    if (context.expiresAt <= Date.now()) { this.context = null; return false; }
    if (!context.suppressedEvents.has(event.type)) return false;
    if (event.type === "decision.changed" && !this.matchesDecisionPayload(event.payload, context.decisionId)) return false;
    if (event.type === "decision.activityAdded" &&
        (!this.matchesDecisionPayload(event.payload, context.decisionId) || !this.matchesActivityEcho(event.payload, context))) return false;
    context.suppressedEvents.delete(event.type);
    if (!context.suppressedEvents.size) this.context = null;
    return true;
  }
  matchesDecisionPayload(payload: unknown, decisionId: string): boolean {
    return this.readString(this.asRecord(payload), "decisionId") === decisionId;
  }
  matchesActivityEcho(payload: unknown, context: PendingActionEchoContext): boolean {
    const record = this.asRecord(payload);
    const eventId = this.readString(record, "eventId");
    const trace = this.asRecord(record?.["tracePayload"]);
    const clientTraceId = this.readString(trace, "ClientTraceId") ?? this.readString(trace, "clientTraceId");
    return !!((context.activityId && eventId === context.activityId) ||
      (context.clientTraceId && clientTraceId === context.clientTraceId));
  }
  asRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
  }
  readString(record: Record<string, unknown> | null, key: string): string | null {
    const value = record?.[key];
    return typeof value === "string" && value.length > 0 ? value : null;
  }
}
